/**
 * @file PlanWorkspace.jsx
 * @module ui/src/components/tenant-admin/PlanWorkspace
 * @description
 *   Provides the top-level layout and presentation orchestration shell for plan
 *   authoring, section navigation, amendment context, and governance dialogs.
 *
 * @version 1.3.0
 * @since S755
 * @changelog
 *   1.0.0  S755  Initial build — Group E batch delivery
 *   1.0.1  S756  OI-S755-C06-CANCEL-SIGNATURE-01 — handleCancelConfirmed zero-arg
 *   1.1.0  S810  OI-S810-SECTION-FORM-STATE — canvasRef wired; handleSave delegates
 *                to PlanDocumentCanvas.handleSave() which calls sectionRef.getValues().
 *                onFieldChange removed from PlanDocumentCanvas props.
 *                PlanWorkspaceHeader.onSave now points to handleSave (not planDoc.onSave).
 *   1.2.0  S815  historyOpen state added. onOpenHistory wired from PlanGovernanceSection
 *                through PlanDocumentCanvas → PlanWorkspace → PlanVersionHistory panel.
 *   1.3.0  S877  OI-S872-CPL-SET-PLAN-TYPE — SetPlanTypeBanner added. Renders when
 *                plan.sourceType='library' and plan is draft. Fetches GET /api/v1/plan-types,
 *   1.4.0  S1020 OI-NEW-PLAN-PLAN-TYPE-SELECTOR — sourceType='library' gate removed.
 *                Banner now renders for all draft plans (webapp/wizard/library) without
 *                plan type set. Banner text updated to be source-type-agnostic.
 *                presents dropdown, calls POST /api/v1/plan-components/{id}/set-plan-type.
 *                On success reloads plan via planDoc.load(). Required before EP6 submit gate.
 *   1.4.0  S900  handleGovernanceSubmit: amendmentInFlight RETIRED (OD-19 S899).
 *                Replaced with governance.amendment which is correctly populated from
 *                EP3 on V_current workspace. EP57 routing now fires correctly for
 *                open|rejected amendment status. Fixes EP57 never being called.
 */

import {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';

import PlanWorkspaceHeader from './PlanWorkspaceHeader';
import AmendmentStateBanner from './AmendmentStateBanner';
import PlanDocumentNavigator from './PlanDocumentNavigator';
import PlanDocumentCanvas from './PlanDocumentCanvas';
import PlanVersionHistory from './PlanVersionHistory';
import MatchingPopulationDrawer from './MatchingPopulationDrawer';

import SaveAsNewPlanDialog from './SaveAsNewPlanDialog';
import CreateCertifiedPlanCandidateDialog from './CreateCertifiedPlanCandidateDialog';
import ProposeAmendmentDialog from './ProposeAmendmentDialog';
import CancelAmendmentDialog from './CancelAmendmentDialog';
import SubmissionReadinessPanel from './SubmissionReadinessPanel';

import usePlanDocument from '../../hooks/usePlanDocument';
import usePlanGovernance from '../../hooks/usePlanGovernance';
import usePlanValidation from '../../hooks/usePlanValidation';

// ---------------------------------------------------------------------------
// SetPlanTypeBanner — S877 D4 (OI-S872-CPL-SET-PLAN-TYPE), updated S1020 (OI-NEW-PLAN-PLAN-TYPE-SELECTOR)
// Renders for any draft plan where plan type has not yet been set (planTypeSet=false).
// sourceType gate removed S1020 — applies to webapp, wizard, and library source types.
// Fetches GET /api/v1/plan-types, presents dropdown, posts set-plan-type.
// Authority: plan_type_router.py GET /api/v1/plan-types + POST set-plan-type.
// On success: calls onSuccess() which triggers planDoc.load() in parent.
// ---------------------------------------------------------------------------
function SetPlanTypeBanner({ planComponentId, onSuccess }) {
  const [planTypes,    setPlanTypes]    = useState([]);
  const [selectedType, setSelectedType] = useState('');
  const [loading,      setLoading]      = useState(true);
  const [submitting,   setSubmitting]   = useState(false);
  const [error,        setError]        = useState('');
  const [dismissed,    setDismissed]    = useState(false);

  useEffect(() => {
    let active = true;
    async function fetchPlanTypes() {
      try {
        const res = await fetch('/api/v1/plan-types', { credentials: 'include' });
        if (!res.ok) throw new Error(`Unable to load plan types (${res.status}).`);
        const data = await res.json();
        // Normalise: array or { planTypes: [] } shape
        const items = Array.isArray(data) ? data : (data.planTypes ?? data.plan_types ?? []);
        const available = items.filter(pt => pt.available !== false);
        if (active) {
          setPlanTypes(available);
          if (available.length > 0) setSelectedType(available[0].plan_type_key ?? available[0].planTypeCode ?? available[0].plan_type_code ?? '');
        }
      } catch (err) {
        if (active) setError(err.message);
      } finally {
        if (active) setLoading(false);
      }
    }
    fetchPlanTypes();
    return () => { active = false; };
  }, []);

  const handleConfirm = useCallback(async () => {
    if (!selectedType || !planComponentId) return;
    setSubmitting(true);
    setError('');
    try {
      const res = await fetch(
        `/api/v1/plan-components/${planComponentId}/set-plan-type`,
        {
          method:      'POST',
          credentials: 'include',
          headers:     { 'Content-Type': 'application/json' },
          body:        JSON.stringify({ plan_type_key: selectedType }),
        }
      );
      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}));
        throw new Error(
          errBody.detail ?? errBody.error ?? `set-plan-type failed (${res.status}).`
        );
      }
      onSuccess();
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  }, [planComponentId, selectedType, onSuccess]);

  if (dismissed) return null;

  return (
    <div className="modifier-manager-info" role="region" aria-label="Plan type configuration required">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <strong>Plan type configuration required</strong>
          <p style={{ margin: '4px 0 0', fontSize: '0.875rem', color: 'rgb(var(--color-text-muted))' }}>
            Select a plan type to configure modifier applicability before submitting for approval.
          </p>
        </div>
        <button
          type="button"
          className="text-button"
          onClick={() => setDismissed(true)}
          aria-label="Dismiss plan type banner"
        >
          Dismiss
        </button>
      </div>

      {loading && (
        <p style={{ fontSize: '0.875rem', color: 'rgb(var(--color-text-muted))' }}>
          Loading plan types…
        </p>
      )}

      {!loading && planTypes.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '12px', flexWrap: 'wrap' }}>
          <select
            style={{ border: '1px solid rgb(var(--color-border))', borderRadius: 6, padding: '7px 10px', font: 'inherit', fontSize: 13, background: 'rgb(var(--color-surface))', color: 'rgb(var(--color-text))', minWidth: 200, boxSizing: 'border-box' }}
            value={selectedType}
            onChange={(e) => setSelectedType(e.target.value)}
            disabled={submitting}
            aria-label="Select plan type"
          >
            {planTypes.filter(pt => (pt.plan_type_key ?? pt.planTypeCode ?? pt.plan_type_code ?? '')).map((pt, idx) => {
              const code  = pt.plan_type_key   ?? pt.planTypeCode  ?? pt.plan_type_code  ?? '';
              const label = pt.label           ?? pt.planTypeName  ?? pt.plan_type_name  ?? code;
              return (
                <option key={code || idx} value={code}>{label}</option>
              );
            })}
          </select>
          <button
            type="button"
            className="primary-button"
            onClick={handleConfirm}
            disabled={submitting || !selectedType}
          >
            {submitting ? 'Configuring…' : 'Set plan type'}
          </button>
        </div>
      )}

      {!loading && planTypes.length === 0 && !error && (
        <p style={{ fontSize: '0.875rem', color: 'rgb(var(--color-text-muted))' }}>
          No plan types available. Contact your administrator.
        </p>
      )}

      {error && (
        <p
          className="modifier-manager-inline-error"
          role="alert"
          style={{ marginTop: '8px' }}
        >
          {error}
        </p>
      )}
    </div>
  );
}

const styles = {
  root: {
    minHeight: '100%',
    display: 'flex',
    flexDirection: 'column',
    fontFamily: '"IBM Plex Sans", "Segoe UI", Arial, sans-serif',
    color: 'rgb(var(--color-text))',
    background: 'rgb(var(--color-bg))',
  },
  body: {
    display: 'flex',
    flex: 1,
    minHeight: 0,
  },
  canvas: {
    flex: 1,
    minWidth: 0,
    overflow: 'auto',
  },
  loading: {
    minHeight: '420px',
    padding: '24px',
    background: 'rgb(var(--color-bg))',
  },
  skeletonLine: {
    height: '18px',
    marginBottom: '12px',
    borderRadius: '4px',
    background: 'rgb(var(--color-surface-muted))',
  },
  error: {
    margin: '24px',
    padding: '16px',
  },
  historyPanel: {
    borderTop: '1px solid rgb(var(--color-border))',
    padding: '24px',
    background: 'rgb(var(--color-bg))',
  },
  historyPanelHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
  },
};

export default function PlanWorkspace({
  planId,
  currentUser,
}) {
  const [activeSection, setActiveSection] = useState('overview');
  const [saveAsOpen,     setSaveAsOpen]     = useState(false);
  const [templateOpen,   setTemplateOpen]   = useState(false);
  const [amendmentOpen,  setAmendmentOpen]  = useState(false);
  const [cancelOpen,     setCancelOpen]     = useState(false);
  const [readinessOpen,  setReadinessOpen]  = useState(false);
  const [activeVersion,  setActiveVersion]  = useState('draft');
  const [compareActive,  setCompareActive]  = useState(false);
  // S815: historyOpen — controls inline PlanVersionHistory panel from Review & Submit
  const [historyOpen,    setHistoryOpen]    = useState(false);
  // S816: populationOpen — controls MatchingPopulationDrawer (OI-S815-MATCHINGPOPULATION-TRIGGER)
  const [populationOpen, setPopulationOpen] = useState(false);

  const canvasRef = useRef(null);

  const planDoc    = usePlanDocument(planId);
  const governance = usePlanGovernance(planId, currentUser);
  const validation = usePlanValidation(planId);

  const handleSectionSelect = (sectionKey) => {
    setActiveSection(sectionKey);
  };

  // S972 OI-UI-11: async — canvas.handleSave() is now async (hybrid weight PATCH
  // fires inside MeasurementEconomicsSection.getValues() before plan PATCH).
  // Non-hybrid sections unaffected — awaiting a sync return is a no-op.
  const handleSave = async () => {
    await canvasRef.current?.handleSave();
  };

  const handleSubmitRequest = async () => {
    // S818: In amendment mode, validate V_new (successorPlanId) not V_current.
    // Pass override at call time — document is loaded by the time user clicks submit.
    // S900: amendmentInFlight RETIRED (OD-19 S899) — use governance.amendment instead.
    const successorId = governance.amendment?.successorPlanId ?? null;
    await validation.runValidation('pre_submit', successorId);
    setReadinessOpen(true);
  };

  const handleOpenSaveAs    = () => setSaveAsOpen(true);
  const handleOpenTemplate  = () => setTemplateOpen(true);
  const handleOpenAmendment = () => setAmendmentOpen(true);
  const handleOpenCancel    = () => setCancelOpen(true);
  // S815: open/close full version history panel
  const handleOpenHistory    = () => setHistoryOpen(true);
  const handleCloseHistory   = () => setHistoryOpen(false);
  // S816: population drawer handlers (OI-S815-MATCHINGPOPULATION-TRIGGER)
  const handleOpenPopulation  = () => setPopulationOpen(true);
  const handleClosePopulation = () => setPopulationOpen(false);

  const handleCloseSaveAs    = () => setSaveAsOpen(false);
  const handleCloseTemplate  = () => setTemplateOpen(false);
  const handleCloseAmendment = () => setAmendmentOpen(false);
  const handleCloseCancel    = () => setCancelOpen(false);
  const handleCloseReadiness = () => setReadinessOpen(false);

  const handleForkCreated     = () => setSaveAsOpen(false);
  const handleTemplateCreated = () => setTemplateOpen(false);
  const handleAmendmentBegun  = () => setAmendmentOpen(false);

  // OI-S900: was setCancelOpen(false) only — governance + planDoc stayed stale after cancel.
  const handleCancelConfirmed = useCallback(async () => {
    setCancelOpen(false);
    await Promise.all([governance.refresh(), planDoc.load()]);
  }, [governance, planDoc]);

  // S1020: planTypeSet is an optimistic local flag — suppresses the banner immediately
  // after set-plan-type succeeds without waiting for planDoc.load() to complete.
  // The authoritative signal is planDoc.document.planComponent.modifierApplicabilityConfirmed
  // (DB-backed, NN bool, FALSE at creation, TRUE after upsert_plan_type() completes).
  // Both are checked in the banner render condition — banner hides when EITHER is true.
  // This prevents the banner reappearing on page reload after plan type was already set.
  const [planTypeSet, setPlanTypeSet] = useState(false);
  const handlePlanTypeSet = useCallback(() => {
    setPlanTypeSet(true);
    planDoc.load();
  }, [planDoc]);

  const handleGovernanceSubmit = async () => {
    // S818 — OI-S817-EP57-SUBMIT-ROUTING fix.
    // When plan is V_current with an active amendment, route to EP57
    // (POST /plans/{id}/amendments/{aid}/submit) instead of EP6
    // (POST /plans/{id}/submit). Section 0.11: these are separate code paths.
    // S900: amendmentInFlight RETIRED (OD-19 S899) — use governance.amendment instead.
    // governance.amendment is populated from EP3 on V_current workspace and carries
    // amendmentId + amendmentStatus. EP57 accepts open|rejected as source states.
    // Prior check (aif?.hasAmendment && aif?.amendmentId) was always null post-OD-19,
    // causing EP57 to never fire and EP6 to be called for amendment submits instead.
    const amd = governance.amendment;
    if (amd?.amendmentId && (amd?.amendmentStatus === 'open' || amd?.amendmentStatus === 'rejected')) {
      // EP57 — amendment submit (open|rejected → pending_approval)
      try {
        const response = await fetch(
          `/api/v1/plans/${planId}/amendments/${amd.amendmentId}/submit`,
          {
            method:      'POST',
            credentials: 'include',
            headers:     { 'Content-Type': 'application/json' },
            body:        JSON.stringify({}),
          }
        );
        if (!response.ok) {
          const errBody = await response.json().catch(() => ({}));
          throw new Error(
            errBody.detail || `Amendment submission failed (${response.status}).`
          );
        }
        // Response shape: { amendmentId, amendmentStatus, submittedBy, submittedAt, validationStatus }
        // Refetch governance + plan document so banner updates to pending_approval.
        await Promise.all([governance.refresh(), planDoc.load()]);
      } catch (err) {
        // Surface error to console; panel stays open so user can retry.
        console.error('[EP57] Amendment submit failed:', err.message);
        return;
      }
    } else {
      // EP6 — standard plan submit (existing behavior — do not break)
      await governance.onSubmit();
      // OI-S900: planDoc.load() was missing here — plan status badge stayed stale after EP6 submit.
      // governance.onSubmit() already calls load() (EP3 refresh) but planDoc needs its own reload.
      await planDoc.load();
    }
    setReadinessOpen(false);
  };

  if (planDoc.loading) {
    return (
      <div
        style={styles.loading}
        aria-busy="true"
        aria-label="Loading plan workspace"
        data-component="PlanWorkspaceLoading"
      >
        <div style={{ ...styles.skeletonLine, width: '45%' }} />
        <div style={{ ...styles.skeletonLine, width: '70%' }} />
        <div style={{ ...styles.skeletonLine, width: '58%' }} />
        <div style={{ ...styles.skeletonLine, width: '82%' }} />
      </div>
    );
  }

  if (planDoc.error) {
    return (
      <div
        style={styles.error}
        className="modifier-manager-inline-error"
        role="alert"
        data-component="PlanWorkspaceError"
      >
        <p>{planDoc.error}</p>
        <button
          type="button"
          className="secondary-button"
          onClick={planDoc.load}
        >
          Retry
        </button>
      </div>
    );
  }

  if (!planDoc.document) {
    return (
      <div
        style={styles.error}
        className="modifier-manager-inline-error"
        role="alert"
      >
        Plan document is unavailable.
      </div>
    );
  }

  const plan     = planDoc.document.plan;
  const sections = planDoc.document.sections || [];

  const validationErrors = validation.validationResult?.errors || {};

  const validationState = {
    hasBlockingErrors: Boolean(validation.validationResult?.hasBlockingErrors),
    blockingCount:     validation.validationResult?.blockingCount || 0,
    warningCount:      validation.validationResult?.warningCount  || 0,
  };

  const amendmentHeaderState = {
    inAmendmentMode:        governance.inAmendmentMode,
    draftVersion:           governance.amendment?.draftVersion ?? null,
    amendmentEffectiveDate: governance.amendment?.effectiveDate ?? null,
  };

  return (
    <div
      style={styles.root}
      data-component="PlanWorkspace"
    >
      <PlanWorkspaceHeader
        plan={plan}
        saveState={planDoc.saveState}
        validationState={validationState}
        amendmentState={amendmentHeaderState}
        currentUser={currentUser}
        canSubmit={governance.canSubmit}
        canProposeAmendment={governance.canProposeAmendment}
        canCreateTemplate={governance.canCreateTemplate}
        onSave={handleSave}
        onSubmit={handleSubmitRequest}
        onSaveAs={handleOpenSaveAs}
        onSaveAsTemplate={handleOpenTemplate}
        onProposeAmendment={handleOpenAmendment}
        onCancelAmendment={handleOpenCancel}
      />

      {governance.inAmendmentMode && governance.amendment && (
        <AmendmentStateBanner
          amendment={governance.amendment}
          activeVersion={activeVersion}
          compareActive={compareActive}
          isActor={Boolean(
            governance.amendment.isActor ??
              governance.amendment.canAct ??
              false
          )}
          onVersionSwitch={setActiveVersion}
          onCompareToggle={setCompareActive}
          onCancelAmendment={handleOpenCancel}
          onSubmitAmendment={handleSubmitRequest}
        />
      )}

      {/* S1020: SetPlanTypeBanner — OI-S872-CPL-SET-PLAN-TYPE + OI-NEW-PLAN-PLAN-TYPE-SELECTOR.
           Shown for ANY draft plan where plan type has not yet been set (zero PCMA rows).
           sourceType === 'library' gate removed — spec §3.1: "PCMA row existence is a proxy
           for set-plan-type was called." Applies equally to webapp/wizard/library source types.
           EP7 sets source_type='webapp' for all API-created plans (spec EP7 note, line 1764).
           planComponent.plan_component_id (or planComponentId) required for POST. */}
      {plan.planStatus === 'draft' &&
        planDoc.document?.planComponent &&
        !planDoc.document.planComponent.modifierApplicabilityConfirmed &&
        !planTypeSet && (
          <SetPlanTypeBanner
            planComponentId={
              planDoc.document.planComponent.planComponentId ??
              planDoc.document.planComponent.plan_component_id
            }
            onSuccess={handlePlanTypeSet}
          />
        )}

      <div style={styles.body}>
        <PlanDocumentNavigator
          sections={sections}
          activeSection={activeSection}
          onSectionSelect={handleSectionSelect}
        />

        <main style={styles.canvas}>
          <PlanDocumentCanvas
            ref={canvasRef}
            activeSection={activeSection}
            document={planDoc.document}
            amendmentMode={governance.inAmendmentMode}
            readOnly={planDoc.readOnly}
            save={planDoc.save}
            onSectionNavigate={handleSectionSelect}
            onOpenHistory={handleOpenHistory}
            onViewPopulation={handleOpenPopulation}
            validationErrors={validationErrors}
            planId={planId}
            planComponentId={
              planDoc.document?.planComponent?.planComponentId ??
              planDoc.document?.planComponent?.plan_component_id ??
              null
            }
          />

          {/* S815: Full version history panel — rendered below canvas when open.
               PlanVersionHistory fetches EP18 + EP19 independently. */}
          {historyOpen && plan?.planId && (
            <div style={styles.historyPanel}>
              <div style={styles.historyPanelHeader}>
                <h3 style={{ margin: 0 }}>Full version history</h3>
                <button
                  type="button"
                  className="text-button"
                  onClick={handleCloseHistory}
                >
                  Close history
                </button>
              </div>
              <PlanVersionHistory
                planId={plan.planId}
                currentVersion={plan.version ?? 1}
              />
            </div>
          )}
        </main>
      </div>

      {/* S816: Matching population drawer — OI-S815-MATCHINGPOPULATION-TRIGGER */}
      {plan?.planId && (
        <MatchingPopulationDrawer
          planId={plan.planId}
          open={populationOpen}
          onClose={handleClosePopulation}
        />
      )}

      {saveAsOpen && (
        <SaveAsNewPlanDialog
          sourcePlan={plan}
          open={saveAsOpen}
          onCreated={handleForkCreated}
          onCancel={handleCloseSaveAs}
        />
      )}

      {templateOpen && (
        <CreateCertifiedPlanCandidateDialog
          sourcePlan={plan}
          open={templateOpen}
          onCreated={handleTemplateCreated}
          onCancel={handleCloseTemplate}
        />
      )}

      {amendmentOpen && (
        <ProposeAmendmentDialog
          plan={plan}
          open={amendmentOpen}
          onBegun={handleAmendmentBegun}
          onCancel={handleCloseAmendment}
        />
      )}

      {cancelOpen && governance.amendment && (
        <CancelAmendmentDialog
          planId={planId}
          amendment={governance.amendment}
          approvedVersion={governance.amendment.approvedVersion}
          open={cancelOpen}
          onConfirm={handleCancelConfirmed}
          onAbort={handleCloseCancel}
        />
      )}

      {readinessOpen && (
        <SubmissionReadinessPanel
          validationResult={validation.panelValidationResult}
          decisionContext={validation.panelDecisionContext}
          submitting={governance.submitting}
          onNavigateToSection={(sectionKey) => {
            setActiveSection(sectionKey);
            setReadinessOpen(false);
          }}
          onSubmit={handleGovernanceSubmit}
          onClose={handleCloseReadiness}
        />
      )}
    </div>
  );
}

PlanWorkspace.propTypes = {
  planId:      PropTypes.number.isRequired,
  currentUser: PropTypes.object.isRequired,
};
